import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { of } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { SubSink } from "subsink";
import { environment } from "../environments/environment";
import { AuthService } from "./auth/auth.service";
import { UserModel } from "./core/user/user-model";
import { UserRoleModel } from "./core/user/user-role-model";
import { UserService } from "./core/user/user.service";

@Component({
  selector: "app-root",
  template: `
    <dev-controller *ngIf="!isProduction"></dev-controller>
    <app-toast key="app"></app-toast>
    <app-loader></app-loader>
    <app-messages key="appMessage"></app-messages>
    <router-outlet></router-outlet>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  private isUserPilotIdentified = false;
  constructor(private router: Router, private authService: AuthService, private userService: UserService) {
  }

  ngOnInit(): void {
    if (environment.userPilotToken) {
      this.sink.add(this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.initializeUserPilot(event.urlAfterRedirects);
      }));
    }
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }


  get isProduction(): boolean {
    return environment.production;
  }

  private initializeUserPilot(urlPath: string): void {
    const userpilot = (window as any).userpilot;
    if (userpilot && this.authService.isAuthenticated) {
      if (!this.isUserPilotIdentified) {
        this.identifyUserPilot(userpilot, urlPath);
      }
      setTimeout(() => userpilot.reload(), 500); // Allow Angular to render content.
    }
  }

  // Identify UserPilot with UserInfo
  private identifyUserPilot(userpilot: any, urlPath: string): void {
    const user = this.authService.user;
    // Flag up front so navigation while the roles request is in flight can't queue a duplicate lookup.
    this.isUserPilotIdentified = true;

    this.sink.add(this.userService.getUser(user.userId).pipe(
      map((userInfo: UserModel) => userInfo.userRoleModels || []),
      catchError(() => of([] as UserRoleModel[]))
    ).subscribe(userRoleModels => {
      const distinctRoleName = [...new Set(userRoleModels.map(x => x.roleName))];
      const distinctRoleTypeName = [...new Set(userRoleModels.map(x => x.roleTypeName))];
      const chaseMatch = urlPath.match(/\/chase(?:v2)?\/(\d+)/);
      const chaseId = chaseMatch ? chaseMatch[1] : null;
      const projectMatch = urlPath.match(/\/project\/(\d+)/);
      const projectId = projectMatch ? projectMatch[1] : null;
      userpilot.identify(user.userId.toString(), {
        name: `${user.firstName} ${user.lastName}`,
        email: user.emailAddress,
        company: {
          id: user.organizationId?.toString(),
          name: user.organizationName,
        },
        projectId,
        chaseId,
        role: distinctRoleName.join(", "),
        roleType: distinctRoleTypeName.join(", "),
      });
    }));
  }
}
